import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { TSTreeAction, TSTreeNode } from './ts-tree-menu.types';
import { Router } from '@angular/router';

@Component({
  selector: 'ts-tree-menu',
  templateUrl: './ts-tree-menu.component.html',
  styleUrls: ['./ts-tree-menu.component.scss'],
})
export class TSTreeMenuComponent implements OnInit {
  private _items: TSTreeNode[];

  @Input() title: string;
  @Input() leafIcon: string = 'folder';
  @Input() allowNewItems: boolean = true;
  @Input() actions: TSTreeAction[] = [];
  @Input() loading: boolean = false;
  @Input() activeNodeFn: (...args: any[]) => void;
  @Input() disableNodeFn: (...args: any[]) => boolean;
  @Input() activeLeafNodeId: number;
  @Input() isActiveLeafNodeFn: (...args: any[]) => boolean;
  @Input() delegateLeafClickFn?: (route: string) => void;
  /**
   * @deprecated In future uses of the TSTreeMenuComponent, the header and other actions
   * should be controlled by the parent component.
   *
   * @example in here the header is suppressed, and the parent component is responsible for it:
   * ```
   * <ts-page sidenavTitle="Settings">
   * <ts-tree-menu sidenav [items]="options" [suppressHeader]="true"></ts-tree-menu>
   * <router-outlet main></router-outlet>
   * </ts-page>
   * ```
   */
  @Input() suppressHeader: boolean = false;

  @Input() set items(items: TSTreeNode[]) {
    this._items = [...items];
    if (this.allowNewItems) {
      this.appendNewItemButtons(this._items);
    }
    this.dataSource.data = this._items;
    this.expandActiveNode();
  }

  @Output() action = new EventEmitter<TSTreeAction>();

  private _transformer = (node: TSTreeNode, level: number) => {
    return {
      expandable: node.expandable || (!!node.children && node.children.length > 0),
      level,
      badge: node.badge,
      id: node.id,
      name: node.name,
      link: node.link,
      icon: node.icon,
      action: node.action,
    };
  };

  treeControl = new FlatTreeControl<TSTreeNode>(
    (node) => node.level,
    (node) => node.expandable,
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children,
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  hasChild = (_: number, node: TSTreeNode) => node.expandable;

  isActionButton = (_: number, node: TSTreeNode) => node.action && node.action.key;

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.dataSource.data = this._items;
    this.expandActiveNode();
  }

  private appendNewItemButtons(nodes: TSTreeNode[], parent?: TSTreeNode) {
    if (parent) {
      const actionButton = {
        id: 'new-item',
        name: 'New Item',
        link: '',
        icon: 'add',
        action: {
          key: 'new-item',
          label: 'Add New',
          meta: { parent },
        },
      };
      nodes.push(actionButton);
    }

    nodes.forEach((node) => {
      if ((node.expandable || (node.children && node.children.length > 0)) && !node.hideAddNew) {
        this.appendNewItemButtons(node.children, node);
      }
    });
  }

  leafItemClicked(node: TSTreeNode): void {
    if (this.isLeafItemActive(node)) {
      return;
    }

    if (this.delegateLeafClickFn) {
      this.delegateLeafClickFn(node.link);
      return;
    }

    this.router.navigateByUrl(node.link);
  }

  isLeafItemActive(node: TSTreeNode) {
    if (this.isActiveLeafNodeFn) {
      return this.isActiveLeafNodeFn(node);
    }

    const isActive = this.router.url.includes(node.link);
    return isActive;
  }

  expandActiveNode() {
    const activeNode = this.getActiveNode();
    if (!activeNode) return;
    const parent = this.getParentNode(activeNode);
    this.treeControl.expand(parent);
  }

  getActiveNode(): TSTreeNode | undefined {
    if (!this.activeNodeFn) return;
    const activeNode = this.treeControl.dataNodes.find(this.activeNodeFn);
    return activeNode;
  }

  getParentNode(node: TSTreeNode): TSTreeNode | null {
    const currentLevel = node.level;

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.findIndex((n) => n.id === node.id);

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (currentNode.level < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  leaftActionItemClicked(node: TSTreeNode) {
    this.action.emit(node.action);
  }

  isLeafItemDisabled(node): boolean {
    if (this.disableNodeFn) {
      return this.disableNodeFn(node);
    }
    return false;
  }

  onActionClicked(action: TSTreeAction) {
    this.action.emit(action);
  }

  getDataTestId(node: TSTreeNode) {
    return `tree-menu-item-${node.name.toLowerCase().replace(/ /g, '-')}`;
  }
}
